<div>
  <div class="container-fluid">
    <!-- Page Header -->
    <div class="row mb-4">
      <div class="col-md-6">
        <h1 class="h2 mb-1 fw-bold">Chemicals</h1>
        <p class="text-muted">Manage ADN recommended chemicals for spray records</p>
      </div>
      <div class="col-md-6 text-md-end">
        <button type="button" class="btn btn-outline-secondary me-2" (click)="openImportModal()">
          <i class="bi bi-upload me-2"></i>
          Import
        </button>
        <button type="button" class="btn btn-primary" (click)="createChemical()">
          <i class="bi bi-plus-circle me-2"></i>
          Create Chemical
        </button>
      </div>
    </div>

    <!-- Success Message -->
    <div *ngIf="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
      <i class="bi bi-check-circle-fill me-2"></i>
      {{ successMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="successMessage = ''"></button>
    </div>

    <!-- Error Message -->
    <div *ngIf="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="errorMessage = ''"></button>
    </div>

    <!-- Filters & Search -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="row g-3">
          <!-- Search -->
          <div class="col-md-4">
            <label class="form-label small text-body-secondary">Search</label>
            <div class="input-group">
              <span class="input-group-text">
                <i class="bi bi-search"></i>
              </span>
              <input
                type="text"
                class="form-control"
                placeholder="Search by name, EPA number..."
                [(ngModel)]="searchQuery"
                (keyup.enter)="onSearch()"
              />
              <button class="btn btn-outline-secondary" type="button" (click)="onSearch()">
                Search
              </button>
            </div>
          </div>

          <!-- Category Filter -->
          <div class="col-md-2">
            <label class="form-label small text-body-secondary">Category</label>
            <select class="form-select" [(ngModel)]="filterCategory" (change)="onFilterChange()">
              <option *ngFor="let cat of categoryOptions" [value]="cat.value">{{ cat.label }}</option>
            </select>
          </div>

          <!-- Signal Word Filter -->
          <div class="col-md-2">
            <label class="form-label small text-body-secondary">Signal Word</label>
            <select class="form-select" [(ngModel)]="filterSignalWord" (change)="onFilterChange()">
              <option *ngFor="let sw of signalWordOptions" [value]="sw.value">{{ sw.label }}</option>
            </select>
          </div>

          <!-- Featured Filter -->
          <div class="col-md-2">
            <label class="form-label small text-body-secondary">Featured</label>
            <select class="form-select" [(ngModel)]="filterFeatured" (change)="onFilterChange()">
              <option *ngFor="let opt of featuredOptions" [value]="opt.value">{{ opt.label }}</option>
            </select>
          </div>

          <!-- Sponsor Filter -->
          <div class="col-md-2">
            <label class="form-label small text-body-secondary">Sponsor</label>
            <select class="form-select" [(ngModel)]="filterSponsor" (change)="onFilterChange()">
              <option *ngFor="let opt of sponsorOptions" [value]="opt.value">{{ opt.label }}</option>
            </select>
          </div>
        </div>

        <!-- Clear Filters Button -->
        <div class="row mt-3" *ngIf="hasActiveFilters()">
          <div class="col-12">
            <button type="button" class="btn btn-sm btn-outline-secondary" (click)="clearFilters()">
              <i class="bi bi-x-circle me-1"></i>
              Clear Filters
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Chemicals Table -->
    <div *ngIf="!loading" class="card overflow-hidden">
      <div class="table-responsive">
        <table class="table data-table table-hover mb-0">
          <thead>
            <tr>
              <th>Name</th>
              <th>EPA Number</th>
              <th>Manufacturer</th>
              <th>Category</th>
              <th>Signal Word</th>
              <th>Featured</th>
              <th>Sponsor</th>
              <th class="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngIf="chemicals.length === 0">
              <td colspan="8" class="text-center text-muted py-4">
                No chemicals found
              </td>
            </tr>
            <tr *ngFor="let chemical of chemicals" class="cursor-pointer" (click)="viewChemical(chemical)">
              <td>
                <div class="d-flex align-items-center gap-3">
                  <div class="entity-avatar">
                    <i class="bi bi-droplet-half"></i>
                  </div>
                  <div>
                    <div class="fw-medium">{{ chemical.name }}</div>
                    <small class="text-muted" *ngIf="chemical.activeIngredient">{{ chemical.activeIngredient }}</small>
                  </div>
                </div>
              </td>
              <td>
                <span *ngIf="chemical.epaNumber">{{ chemical.epaNumber }}</span>
                <span *ngIf="!chemical.epaNumber" class="text-muted">-</span>
              </td>
              <td>
                <span *ngIf="chemical.manufacturer">{{ chemical.manufacturer }}</span>
                <span *ngIf="!chemical.manufacturer" class="text-muted">-</span>
              </td>
              <td>
                <span>{{ getCategoryLabel(chemical.category) }}</span>
              </td>
              <td>
                <span *ngIf="chemical.signalWord" class="badge" [ngClass]="getSignalWordClass(chemical.signalWord)">
                  {{ chemical.signalWord }}
                </span>
                <span *ngIf="!chemical.signalWord" class="text-muted">-</span>
              </td>
              <td>
                <span *ngIf="chemical.isFeatured" class="badge bg-success">
                  <i class="bi bi-star-fill me-1"></i>
                  Featured
                </span>
                <span *ngIf="!chemical.isFeatured" class="text-muted">-</span>
              </td>
              <td>
                <span *ngIf="chemical.sponsorName">{{ chemical.sponsorName }}</span>
                <span *ngIf="!chemical.sponsorName" class="text-muted">-</span>
              </td>
              <td class="text-end">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary me-1"
                  (click)="viewChemical(chemical); $event.stopPropagation()"
                  title="View"
                >
                  <i class="bi bi-eye"></i>
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  (click)="editChemical(chemical.id); $event.stopPropagation()"
                  title="Edit"
                >
                  <i class="bi bi-pencil"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="card-footer" *ngIf="totalPages > 1">
        <div class="d-flex justify-content-between align-items-center">
          <div class="text-muted small">
            Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, totalChemicals) }} of {{ totalChemicals }} chemicals
          </div>
          <nav>
            <ul class="pagination pagination-sm mb-0">
              <li class="page-item" [class.disabled]="currentPage === 1">
                <a class="page-link" (click)="goToPage(currentPage - 1)">
                  <i class="bi bi-chevron-left"></i>
                </a>
              </li>
              <li
                *ngFor="let page of paginationRange"
                class="page-item"
                [class.active]="page === currentPage"
              >
                <a class="page-link" (click)="goToPage(page)">{{ page }}</a>
              </li>
              <li class="page-item" [class.disabled]="currentPage === totalPages">
                <a class="page-link" (click)="goToPage(currentPage + 1)">
                  <i class="bi bi-chevron-right"></i>
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Import Modal -->
<app-chemical-import-modal
  *ngIf="showImportModal"
  (close)="closeImportModal()"
  (importSuccess)="onImportSuccess($event)"
></app-chemical-import-modal>
